import { subMinutes, subHours } from 'date-fns'

export interface SiteAlert {
  id: string
  siteId: string
  siteName: string
  type: 'over-sla' | 'congestion' | 'pedestrian' | 'smoking'
  zone: string
  severity: 'medium' | 'high'
  timestamp: Date
  details: string
}

export interface Site {
  id: string
  name: string
  city: string
  state: string
  status: 'normal' | 'attention' | 'critical'
  trucksInside: number
  trucksOverSLA: number
  trucksApproachingSLA: number
  congestionLevel: 'low' | 'medium' | 'high'
  avgTimeInside: number // minutes
  longestTruckDuration: number // minutes
  trend: 'up' | 'down' | 'stable'
  alerts: SiteAlert[]
  guards: { onDuty: number; expected: number; missingExit: number }
}

export interface WatchedVehicle {
  id: string
  plate: string
  sitesVisited: string[]
  totalVisits: number
  warningsIssued: number
  lastVisit: Date
  currentStatus: 'inside' | 'outside'
  currentSite?: string
  riskLevel: 'low' | 'medium' | 'high'
}

const NOW = new Date()

export const MOCK_SITES: Site[] = [
  {
    id: 's1',
    name: 'Lucknow Hub',
    city: 'Lucknow',
    state: 'Uttar Pradesh',
    status: 'normal',
    trucksInside: 42,
    trucksOverSLA: 0,
    trucksApproachingSLA: 3,
    congestionLevel: 'low',
    avgTimeInside: 145,
    longestTruckDuration: 210,
    trend: 'stable',
    alerts: [],
    guards: { onDuty: 8, expected: 8, missingExit: 0 },
  },
  {
    id: 's2',
    name: 'Delhi NCR',
    city: 'Gurugram',
    state: 'Haryana',
    status: 'critical',
    trucksInside: 85,
    trucksOverSLA: 12,
    trucksApproachingSLA: 15,
    congestionLevel: 'high',
    avgTimeInside: 280,
    longestTruckDuration: 340,
    trend: 'up',
    alerts: [
      {
        id: 'a1',
        siteId: 's2',
        siteName: 'Delhi NCR',
        type: 'congestion',
        zone: 'Main Gate',
        severity: 'high',
        timestamp: subMinutes(NOW, 5),
        details: 'Queue length > 15 vehicles',
      },
      {
        id: 'a2',
        siteId: 's2',
        siteName: 'Delhi NCR',
        type: 'over-sla',
        zone: 'Bay Area',
        severity: 'high',
        timestamp: subMinutes(NOW, 10),
        details: 'Multiple vehicles exceeding 4h dwell time',
      },
    ],
    guards: { onDuty: 12, expected: 15, missingExit: 2 },
  },
  {
    id: 's3',
    name: 'Mumbai Port',
    city: 'Navi Mumbai',
    state: 'Maharashtra',
    status: 'attention',
    trucksInside: 64,
    trucksOverSLA: 4,
    trucksApproachingSLA: 8,
    congestionLevel: 'medium',
    avgTimeInside: 190,
    longestTruckDuration: 260,
    trend: 'up',
    alerts: [
      {
        id: 'a3',
        siteId: 's3',
        siteName: 'Mumbai Port',
        type: 'pedestrian',
        zone: 'Container Yard',
        severity: 'high',
        timestamp: subMinutes(NOW, 25),
        details: 'Unauthorized personnel in restricted zone',
      },
    ],
    guards: { onDuty: 10, expected: 10, missingExit: 1 },
  },
  {
    id: 's4',
    name: 'Bangalore Tech Park',
    city: 'Bangalore',
    state: 'Karnataka',
    status: 'normal',
    trucksInside: 28,
    trucksOverSLA: 0,
    trucksApproachingSLA: 1,
    congestionLevel: 'low',
    avgTimeInside: 110,
    longestTruckDuration: 150,
    trend: 'down',
    alerts: [],
    guards: { onDuty: 6, expected: 6, missingExit: 0 },
  },
  {
    id: 's5',
    name: 'Chennai Logistics',
    city: 'Chennai',
    state: 'Tamil Nadu',
    status: 'attention',
    trucksInside: 55,
    trucksOverSLA: 2,
    trucksApproachingSLA: 6,
    congestionLevel: 'medium',
    avgTimeInside: 175,
    longestTruckDuration: 250,
    trend: 'stable',
    alerts: [
      {
        id: 'a4',
        siteId: 's5',
        siteName: 'Chennai Logistics',
        type: 'smoking',
        zone: 'Fuel Depot',
        severity: 'medium',
        timestamp: subMinutes(NOW, 45),
        details: 'Smoking detected near fuel storage',
      },
    ],
    guards: { onDuty: 9, expected: 10, missingExit: 0 },
  },
  {
    id: 's6',
    name: 'Pune Distribution',
    city: 'Pune',
    state: 'Maharashtra',
    status: 'normal',
    trucksInside: 35,
    trucksOverSLA: 0,
    trucksApproachingSLA: 2,
    congestionLevel: 'low',
    avgTimeInside: 130,
    longestTruckDuration: 180,
    trend: 'stable',
    alerts: [],
    guards: { onDuty: 7, expected: 7, missingExit: 0 },
  },
]

export const MOCK_WATCHED_VEHICLES: WatchedVehicle[] = [
  {
    id: 'v1',
    plate: 'HR55AB1234',
    sitesVisited: ['Delhi NCR', 'Lucknow Hub'],
    totalVisits: 45,
    warningsIssued: 3,
    lastVisit: subHours(NOW, 2),
    currentStatus: 'inside',
    currentSite: 'Delhi NCR',
    riskLevel: 'high',
  },
  {
    id: 'v2',
    plate: 'MH12CD5678',
    sitesVisited: ['Mumbai Port', 'Pune Distribution'],
    totalVisits: 28,
    warningsIssued: 1,
    lastVisit: subHours(NOW, 5),
    currentStatus: 'outside',
    riskLevel: 'medium',
  },
  {
    id: 'v3',
    plate: 'TN09EF9012',
    sitesVisited: ['Chennai Logistics', 'Bangalore Tech Park'],
    totalVisits: 12,
    warningsIssued: 0,
    lastVisit: subHours(NOW, 1),
    currentStatus: 'inside',
    currentSite: 'Chennai Logistics',
    riskLevel: 'low',
  },
  {
    id: 'v4',
    plate: 'UP32GH3456',
    sitesVisited: ['Lucknow Hub', 'Delhi NCR'],
    totalVisits: 67,
    warningsIssued: 5,
    lastVisit: subHours(NOW, 0.5),
    currentStatus: 'inside',
    currentSite: 'Lucknow Hub',
    riskLevel: 'high',
  },
]
